/**
 * Composer · Export / overseas mode.
 *
 * Three persisted fields: legal name, email, and country. Country
 * arrived with migration 0058 — the composer sends it as part of the
 * create payload; the detail cover and address-edit dialog both read
 * it back.
 *
 * Email is mandatory here because WhatsApp delivery isn't reliable
 * cross-border — the export invoice has to be deliverable somehow.
 *
 * @see prototype/sales/customer-new.html — V1.1 export pane baseline
 */
import * as React from "react";
import { Globe2, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, cn } from "@flint/ui";

export interface ComposerExportState {
  name: string;
  /**
   * Country name. Persisted since migration 0058; round-trips to the
   * detail cover and the address-edit dialog. Optional at save time —
   * falls back to blank if the user skips it.
   */
  country: string;
  email: string;
}

export interface ComposerExportProps {
  state: ComposerExportState;
  onChange: (patch: Partial<ComposerExportState>) => void;
}

const COUNTRIES = [
  "United States",
  "United Kingdom",
  "United Arab Emirates",
  "Singapore",
  "Germany",
  "Australia",
  "Canada",
  "Saudi Arabia",
  "Qatar",
  "Netherlands",
  "France",
  "Japan",
];

export function ComposerExport({ state, onChange }: ComposerExportProps) {
  return (
    <section
      aria-label="Export customer"
      className="rounded-md border border-line bg-paper shadow-card"
    >
      <header className="px-[18px] pt-3.5 pb-2 border-b border-line">
        <h3 className="text-sm font-semibold text-ink m-0">Customer details</h3>
        <p className="text-xs text-muted mt-0.5 leading-snug">
          Exports are zero-rated under Section 16 IGST Act. Choose LUT or IGST-paid at invoice time.
        </p>
      </header>

      <div className="px-[18px] py-[18px] flex flex-col gap-3.5">
        <div
          className={cn(
            "flex items-start gap-2 px-3 py-2.5 rounded-md",
            "bg-sky-50 border border-sky-100 text-sky-700 text-xs leading-snug",
          )}
        >
          <Globe2 className="w-3.5 h-3.5 mt-px flex-shrink-0 text-sky-600" aria-hidden />
          <div>
            <strong className="font-semibold">Export customer.</strong> Zero-rated supply under
            Section 16 IGST Act. LUT / IGST-paid choice is made on the export invoice itself, not
            here.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-semibold text-ink-2">
              Legal / business name <span className="text-bad ml-0.5">*</span>
            </span>
            <input
              type="text"
              value={state.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="e.g. Acme Corp (USA)"
              autoFocus
              className={cn(
                "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
                "placeholder:text-muted hover:border-line-2",
                "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
              )}
            />
          </label>
          <label className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-semibold text-ink-2 flex items-center gap-1.5">
              Country <span className="text-bad ml-0.5">*</span>
            </span>
            <Select
              {...(state.country ? { value: state.country } : {})}
              onValueChange={(v) => onChange({ country: v })}
            >
              <SelectTrigger className="h-9 rounded-md text-sm">
                <SelectValue placeholder="Select country…" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <label className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-semibold text-ink-2">
            Email for invoice delivery <span className="text-bad ml-0.5">*</span>
          </span>
          <input
            type="email"
            value={state.email}
            onChange={(e) => onChange({ email: e.target.value })}
            placeholder="accounts@example.com"
            inputMode="email"
            autoComplete="email"
            className={cn(
              "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
              "placeholder:text-muted hover:border-line-2",
              "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
            )}
          />
          <span className="text-xs text-muted leading-snug">
            Required — WhatsApp delivery isn&apos;t reliable for overseas customers.
          </span>
        </label>

        <div
          className={cn(
            "flex items-start gap-2 px-3 py-2.5 rounded-md",
            "bg-sky-50 border border-sky-100 text-sky-700 text-xs leading-snug",
          )}
        >
          <Info className="w-3.5 h-3.5 mt-px flex-shrink-0 text-sky-600" aria-hidden />
          <div>
            <strong className="font-semibold">Shipping address</strong> + currency are captured on
            the export invoice itself, where they actually matter for the shipping bill.
          </div>
        </div>
      </div>
    </section>
  );
}
